import { useMemo } from 'react';
import { DeviationQuestionDto, DeviationQuestionUserResponseDto } from 'src/generated/services/TFinancialApi';
import { processQuestionsRecursively, QuestionFieldConfig } from '../utils/questionMapper';
import { processAnswersToDefaultValues } from '../utils/answerProcessor';

interface UseQuestionnaireDataProps {
  questions: DeviationQuestionDto[];
  answers: DeviationQuestionUserResponseDto[];
  formValues?: Record<string, any>;
  additionalDefaultValues?: Record<string, any>;
}

interface UseQuestionnaireDataReturn {
  fieldConfigs: QuestionFieldConfig[];
  defaultValues: Record<string, any>;
  visibleFieldConfigs: QuestionFieldConfig[];
}

/**
 * Hook to process questionnaire data and return clean, structured field configurations
 */
export const useQuestionnaireData = ({
  questions,
  answers,
  formValues = {},
  additionalDefaultValues = {},
}: UseQuestionnaireDataProps): UseQuestionnaireDataReturn => {
  // First, create a basic mapping of answers to form values for conditional logic
  const answerFormValues = useMemo(() => {
    const values: Record<string, any> = {};
    if (answers) {
      answers.forEach((answer) => {
        if (answer.questionId) {
          switch (answer.questionType) {
            case 'TEXT':
              // For TEXT, use answerOptionId as key to match field config
              if (answer.answerOptionId) {
                values[answer.answerOptionId] = answer.answerText || '';
              }
              break;
            case 'DATE_RANGE':
              // For DATE_RANGE, use answerOptionId as key and parse JSON to object
              if (answer.answerOptionId && answer.answerText) {
                try {
                  const parsedDateRange = JSON.parse(answer.answerText);
                  // Convert string dates to Date objects for the date picker
                  values[answer.answerOptionId] = {
                    start: new Date(parsedDateRange.start),
                    end: new Date(parsedDateRange.end),
                  };
                } catch (error) {
                  console.warn('Failed to parse date range:', answer.answerText);
                  values[answer.answerOptionId] = null;
                }
              }
              break;
            case 'RADIO':
            case 'SELECT':
              if (answer.answerOptionId) {
                values[answer.questionId] = answer.answerOptionId;
              }
              break;
            case 'MULTIPLE_SELECT':
              if (answer.answerOptionId) {
                if (!values[answer.questionId]) {
                  values[answer.questionId] = [];
                }
                values[answer.questionId].push(answer.answerOptionId);
              }
              break;
            default:
              values[answer.questionId] = answer.answerText || answer.answerOptionId;
          }
        }
      });
    }
    return values;
  }, [answers]);

  // Process all possible field configurations from questions using answer values for conditional logic
  const fieldConfigs = useMemo(() => {
    if (!questions || questions.length === 0) return [];
    return processQuestionsRecursively(questions, answerFormValues);
  }, [questions, answerFormValues]);

  // Process answers to default values
  const answersDefaultValues = useMemo(() => processAnswersToDefaultValues(answers, fieldConfigs), [answers, fieldConfigs]);

  // Combine all default values
  const defaultValues = useMemo(
    () => ({
      ...answersDefaultValues,
      ...additionalDefaultValues,
    }),
    [answersDefaultValues, additionalDefaultValues]
  );

  // Memoize form values to prevent unnecessary recalculations
  const memoizedFormValues = useMemo(() => JSON.stringify(formValues || {}), [formValues]);

  // Calculate visible field configurations based on current form values
  const visibleFieldConfigs = useMemo(() => {
    const parsedFormValues = JSON.parse(memoizedFormValues);
    const currentFormValues = { ...defaultValues, ...parsedFormValues };
    return processQuestionsRecursively(questions, currentFormValues);
  }, [questions, defaultValues, memoizedFormValues]);

  return {
    fieldConfigs,
    defaultValues,
    visibleFieldConfigs,
  };
};
